import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Bot, ShieldBan, ShieldOff } from "lucide-react";
import { Button } from "@ui/components/ui/button";
import { Badge } from "@ui/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@ui/components/ui/card";
import {
  useListSystemBotListings,
  useDelistSystemBotListing,
  useEmergencyDisableSystemBotListing,
} from "@tubo/api-models/hooks/system-bot-api.hooks";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/settings/bots")({
  component: SystemBotsPage,
});

function getStatusBadge(status: string) {
  if (status === "published") return <Badge variant="default">已发布</Badge>;
  if (status === "delisted") return <Badge variant="secondary">已下架</Badge>;
  if (status === "rejected") return <Badge variant="destructive">已拒绝</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function SystemBotsPage() {
  const queryClient = useQueryClient();
  const { data: listings, isLoading } = useListSystemBotListings();
  const delistMutation = useDelistSystemBotListing({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["system-bot-listings"] });
        toast.success("已下架");
      },
      onError: () => toast.error("下架失败"),
    },
  });
  const disableMutation = useEmergencyDisableSystemBotListing();

  return (
    <div className="mx-auto max-w-5xl p-6 md:p-8">
      <header className="mb-8">
        <div className="mb-3 flex items-center gap-2 text-primary">
          <Bot className="size-5" />
          <span className="text-sm font-medium">系统智能体管理</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">系统 Bot 市场</h1>
        <p className="mt-2 text-sm text-muted-foreground">管理跨租户分发的系统 Bot 模板，可下架或紧急禁用已安装实例。</p>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">加载中…</p>}

      {!isLoading && (!listings?.listings || listings.listings.length === 0) && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Bot className="mb-3 size-10 text-muted-foreground" />
            <p className="font-medium">暂无系统 Bot</p>
            <p className="mt-1 text-sm text-muted-foreground">系统管理员可发布不含租户私有资源的 Bot 版本到系统市场。</p>
          </CardContent>
        </Card>
      )}

      {listings?.listings && listings.listings.length > 0 && (
        <div className="space-y-3">
          {listings.listings?.map((listing) => (
            <Card key={listing.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                    <Bot className="size-5 text-muted-foreground" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{listing.name}</CardTitle>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      v{listing.version} · {listing.botType === "agent" ? "含业务任务" : "知识问答"}
                      {listing.category ? ` · ${listing.category}` : ""}
                    </p>
                  </div>
                </div>
                {getStatusBadge(listing.status)}
              </CardHeader>
              <CardContent>
                {listing.desc && <p className="text-sm text-muted-foreground">{listing.desc}</p>}
                {listing.releaseNotes && (
                  <p className="mt-2 text-xs text-muted-foreground">发布说明：{listing.releaseNotes}</p>
                )}
                <div className="mt-4 flex gap-2">
                  {listing.status === "published" && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={delistMutation.isPending}
                      onClick={() => {
                        delistMutation.mutate({ id: listing.id });
                      }}
                    >
                      <ShieldOff className="size-4" />
                      下架
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={disableMutation.isPending}
                    onClick={() => {
                      disableMutation.mutate({ id: listing.id }, {
                        onSuccess: (disabledCount) => {
                          toast.success(`已禁用 ${disabledCount} 个安装`);
                        },
                        onError: () => toast.error("操作失败"),
                      });
                    }}
                  >
                    <ShieldBan className="size-4" />
                    紧急禁用安装
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
